import PrerenderReady from '../../components/PrerenderReady';
import CourseSeo from '../../components/course/CourseSeo';
import React, { useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import useUser from '../../hooks/useUser';
import useCheckoutRedirect from '../../hooks/useCheckoutRedirect';
import useCourseCatalog from '../../hooks/useCourseCatalog';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import Reveal from '../../components/course/Reveal';
import StarRating, { TRUSTPILOT_URL } from '../../components/course/StarRating';
import LogoRow from '../../components/course/LogoRow';
import FactStrip from '../../components/course/FactStrip';
import PromoCountdown from '../../components/course/PromoCountdown';
import CurriculumAccordion from '../../components/course/CurriculumAccordion';
import PreviewVideo from '../../components/course/PreviewVideo';
import ScrollProgress from '../../components/course/ScrollProgress';
import CourseLegalDisclaimer from '../../components/course/CourseLegalDisclaimer';
import {
  ROB_STORY,
  NO_CODING_BADGE,
  REVIEW_SHOTS,
  VIDEO_REVIEWS,
} from '../../components/course/courseContent';
import './CoursePage.css';

const { courseStructuredData } = require('../../site/structuredData');
const { getPublicPageSeo } = require('../../site/publicPageSeo');
const SELF_PACED_PATH = '/courses/ai-trading-bootcamp/self-paced';

export const LIVE_COURSE_PATH = '/courses/ai-trading-bootcamp';

/**
 * Self-paced sales page.
 *
 * This page sells the completed AI Bot Builder recording archive. It must not
 * present the upcoming live AI Trading curriculum as already recorded.
 */

const COMPARISON = [
  { feature: 'Completed AI Bot Builder cohort recordings', selfPaced: true, live: true },
  { feature: 'Watch on your own schedule', selfPaced: true, live: true },
  { feature: 'Current October AI Trading curriculum', selfPaced: false, live: true },
  { feature: 'Live sessions with Rob', selfPaced: false, live: true },
  { feature: 'Ask your own questions in class', selfPaced: false, live: true },
  { feature: 'Direct feedback on your setup', selfPaced: false, live: true },
  { feature: 'Cohort leaderboard', selfPaced: false, live: true },
];

const SELF_PACED_DELIVERABLES = [
  {
    title: 'Vibe coding fundamentals',
    detail: 'Use plain-English prompts to create and edit working Python code.',
    img: '/images/revenue/self-paced-bootcamp-hero.webp',
  },
  {
    title: 'AI Bot Builder and backtesting',
    detail: 'Turn a trading idea into a bot, run a backtest, and read the result.',
    img: '/images/revenue/self-paced-bootcamp-archive.webp',
  },
  {
    title: 'Technical analysis and broker deployment',
    detail: 'Work with indicators, troubleshoot code, and connect a stock broker.',
    img: '/images/courses/learn-research-scanners.webp',
  },
  {
    title: 'Crypto and news-based trading',
    detail: 'Build workflows around crypto markets, earnings, and breaking news.',
    img: '/images/courses/learn-multi-markets.webp',
  },
  {
    title: 'Options and futures foundations',
    detail: 'Review common structures, risk controls, and multi-asset examples.',
    img: '/images/courses/learn-understand-automation.webp',
  },
];

const SELF_PACED_SKILLS = [
  'Vibe coding',
  'AI Bot Builder',
  'Python',
  'Backtesting',
  'Technical analysis',
  'Broker deployment',
  'Crypto',
  'News trading',
  'Options',
  'Futures',
];

// This is the stable archive outline buyers receive across completed cohorts.
// Do not derive sales copy from whichever past cohort happens to sort newest:
// development and production contain different historical rows, and disposable
// QA cohorts must never become customer-facing curriculum.
export const SELF_PACED_ARCHIVE_MODULES = [
  {
    id: 'archive-vibe-coding',
    title: 'Vibe Coding Fundamentals',
    durationMinutes: 60,
    outcomes: [
      'Set up a plain-English coding workflow with Claude or Codex.',
      'Create, run, and revise a working Python project without starting from a blank file.',
    ],
  },
  {
    id: 'archive-bot-builder',
    title: 'AI Bot Builder, Backtests, and Your First Deploy',
    durationMinutes: 60,
    outcomes: [
      'Turn a trading idea into a working BotSpot strategy.',
      'Run a backtest, read the result, and prepare a controlled deployment.',
    ],
  },
  {
    id: 'archive-technical-analysis',
    title: 'Technical Analysis, Launch, and Live Trading',
    durationMinutes: 60,
    outcomes: [
      'Use common indicators and AI to inspect a setup.',
      'Connect a supported stock broker and understand the launch workflow.',
    ],
  },
  {
    id: 'archive-crypto-news',
    title: 'Crypto and News-Based Trading',
    durationMinutes: 60,
    outcomes: [
      'Work with crypto markets and event-driven trading ideas.',
      'Use AI to turn earnings, macro events, and headlines into reviewable rules.',
    ],
  },
  {
    id: 'archive-options-futures',
    title: 'Options and Futures Foundations',
    durationMinutes: 60,
    outcomes: [
      'Understand common options structures and multi-leg examples.',
      'Review futures workflows, risk controls, and broker requirements.',
    ],
  },
];

/**
 * What the recordings buyer actually receives. Kept deliberately shorter than
 * the live stack: no live sessions and no competition, because self-paced
 * buyers do not get those (the comparison table says so honestly).
 */
const buildIncluded = (includedProDays) => [
  {
    img: '/images/courses/inc-recordings.webp',
    name: 'The complete recording library',
    detail: 'Completed recordings from prior AI Bot Builder cohorts, available when you enroll.',
  },
  {
    img: '/images/courses/inc-lifetime.webp',
    name: 'Lifetime access',
    detail:
      'The lessons stay in your course portal. Come back whenever you want, and keep new material we add.',
  },
  {
    img: '/images/courses/inc-prompts.webp',
    name: 'Prompts and workflow pack',
    detail:
      'The exact checklists, prompts, and working files used in class, so you can rerun the same workflow yourself.',
  },
  {
    img: '/images/courses/inc-guarantee.webp',
    name: includedProDays
      ? `${includedProDays} days of BotSpot Pro`
      : 'BotSpot course portal access',
    detail: includedProDays
      ? `Use BotSpot Pro for ${includedProDays} days while you work through the recordings.`
      : 'Open the lessons and supporting material from your BotSpot account.',
  },
];

const FAQS = [
  {
    q: 'How is this different from the live bootcamp?',
    a: 'This is the completed AI Bot Builder recording archive from prior cohorts. The live October AI Trading Bootcamp has a newer curriculum, live Q&A, direct feedback, and a cohort competition.',
  },
  {
    q: 'How long do I have access?',
    a: 'The lessons stay in your course portal and you can return to them whenever you want.',
  },
  {
    q: 'Do I need to know how to code?',
    a: 'No. The whole point is directing Claude and Codex in plain English.',
  },
  {
    q: 'Can I move up to the live cohort later?',
    a: 'Yes. Join any future cohort at the price advertised at the time. Talk to the instructor if you want to discuss it.',
  },
];

const CourseSelfPacedPage = () => {
  const { user } = useUser();
  const { startCheckout } = useCheckoutRedirect('course_self_paced');
  const { isLoading, selfPaced, upfrontPlan, pricingError, promoEndsAt, promoTierName } =
    useCourseCatalog();

  const prerenderReady = !isLoading && !pricingError && Boolean(selfPaced?.planKey);
  const courseSchema = prerenderReady
    ? courseStructuredData({
        path: SELF_PACED_PATH,
        name: selfPaced.productName || 'AI Bot Builder Bootcamp recordings',
        description: getPublicPageSeo(SELF_PACED_PATH)?.description,
        plans: [selfPaced],
        isEnrollable: true,
      })
    : null;

  const included = useMemo(
    () => buildIncluded(selfPaced?.includedProDays),
    [selfPaced?.includedProDays],
  );

  // The catalog applies active promotions to the self-paced plan the same way
  // it does to live plans. A promo is only advertised here when this specific
  // plan actually carries a discount and the promotion has not ended.
  const promoActive = Boolean(promoEndsAt && selfPaced?.hasDiscount);

  const track = useCallback(
    (event, extra = {}) => {
      pushEvent({
        event,
        source: window.location.pathname,
        userId: user?.id || user?.sub,
        email: user?.email,
        ...extra,
      });
    },
    [user],
  );

  const buy = useCallback(
    (ctaLocation) => {
      track(ANALYTICS_EVENTS.BOOTCAMP_CHECKOUT_STARTED, {
        cta_location: ctaLocation,
        path: 'self_paced',
        productSlug: 'ai-bootcamp-self-paced',
        planKey: selfPaced?.planKey || 'self-paced',
      });
      return startCheckout({
        products: [{ productSlug: 'ai-bootcamp-self-paced', planKey: selfPaced?.planKey || null }],
        flowContext: 'course_self_paced',
        metadata: {
          origin: {
            flow: 'course_self_paced',
            ctaLocation,
            returnUrl: '/ai-bot-builder-bootcamp/watch?welcome=1',
          },
        },
        contact: { email: user?.email || '', name: user?.name || '' },
      });
    },
    [startCheckout, track, selfPaced, user],
  );

  const buyDisabled = isLoading || Boolean(pricingError) || !selfPaced;

  const facts = useMemo(
    () => [
      { value: String(SELF_PACED_ARCHIVE_MODULES.length), label: 'Core archive modules' },
      { value: 'Start today', label: 'Availability' },
      { value: 'Your pace', label: 'Schedule' },
    ],
    [],
  );

  const priceBlock = (testId) =>
    pricingError || !selfPaced ? (
      <p className="cp-price-error" data-testid="pricing-error" role="alert">
        Pricing is temporarily unavailable. Please refresh, or contact us and we will help you
        enroll.
      </p>
    ) : (
      <div className="cp-hero-price" data-testid={testId}>
        <div className="cp-price-row">
          {selfPaced.hasDiscount && selfPaced.originalDisplay ? (
            <s className="cp-price-was" data-testid="self-paced-was-price">
              {selfPaced.originalDisplay}
            </s>
          ) : null}
          <strong className="cp-price-now">{selfPaced.display}</strong>
          <span className="cp-price-unit">one-time</span>
          {selfPaced.savingsDisplay ? (
            <span className="cp-price-save">Save {selfPaced.savingsDisplay}</span>
          ) : null}
        </div>
      </div>
    );

  return (
    <div className="course-page" data-testid="self-paced-page">
      <PrerenderReady
        ready={prerenderReady}
        state={
          isLoading
            ? 'loading'
            : pricingError
              ? 'error'
              : selfPaced?.planKey
                ? 'catalog'
                : 'unavailable'
        }
      />
      <ScrollProgress />
      <CourseSeo path="/courses/ai-trading-bootcamp/self-paced" />
      <Helmet>
        {courseSchema ? (
          <script type="application/ld+json">{JSON.stringify(courseSchema)}</script>
        ) : null}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQS.map((faq) => ({
              '@type': 'Question',
              name: faq.q,
              acceptedAnswer: { '@type': 'Answer', text: faq.a },
            })),
          })}
        </script>
      </Helmet>

      {promoActive && (
        <div className="cp-promobar" data-testid="promo-bar">
          <div className="cp-promobar-copy">
            <span className="cp-promobar-title">
              {promoTierName || 'Current pricing'}
              {selfPaced.originalDisplay && selfPaced.display ? (
                <>
                  {' · '}
                  <s className="cp-promobar-was" data-testid="promo-was-price">
                    was {selfPaced.originalDisplay}
                  </s>
                  {', now '}
                  <span data-testid="promo-now-price">{selfPaced.display}</span>
                </>
              ) : null}
            </span>
            <PromoCountdown
              endsAt={promoEndsAt}
              label={`${promoTierName || 'Current pricing'} ends in`}
              variant="blocks"
            />
          </div>
        </div>
      )}

      <header className="cp-hero">
        <div className="cp-hero-fx" aria-hidden="true">
          <div className="cp-hero-fx-grid" />
          <div className="cp-hero-fx-orb cp-hero-fx-orb--amber" />
          <div className="cp-hero-fx-orb cp-hero-fx-orb--blue" />
          <div className="cp-hero-fx-particles">
            {Array.from({ length: 10 }, (_, i) => (
              <span key={i} className={`cp-hero-fx-p cp-hero-fx-p--${i + 1}`} />
            ))}
          </div>
        </div>
        <div className="cp-hero-copy">
          <Reveal>
            <p className="cp-eyebrow">Self-paced · Start today</p>
          </Reveal>
          <Reveal delay={60}>
            <h1>AI Bot Builder Bootcamp. Your Schedule.</h1>
          </Reveal>
          <Reveal delay={120}>
            <p className="cp-sub">
              Start with completed recordings from prior AI Bot Builder cohorts. Learn vibe coding,
              bot building, backtesting, technical analysis, broker deployment, crypto, options, and
              futures at your own pace.
            </p>
          </Reveal>
          <Reveal delay={180}>
            <div className="cp-hero-rating">
              <StarRating rating={4.6} />
            </div>
          </Reveal>

          <Reveal delay={240}>
            {/* Never shows an invented price. */}
            {priceBlock('self-paced-price')}
          </Reveal>

          <Reveal delay={300}>
            <div className="cp-hero-cta">
              <button
                type="button"
                className="cp-btn-primary"
                data-testid="buy-self-paced"
                onClick={() => buy('hero')}
                disabled={buyDisabled}
                aria-busy={isLoading || undefined}
              >
                <span className="cp-btn-label">Get the self-paced course</span>
              </button>
              <Link className="cp-btn-ghost" to={LIVE_COURSE_PATH}>
                See the live cohort
              </Link>
            </div>
          </Reveal>
        </div>

        <Reveal delay={160} className="cp-hero-art">
          <img
            src="/images/courses/ai-trading-workspace-hero.webp"
            alt="An AI trading workspace: assistant research on the left, price chart and a trade review card on the right"
            width="1280"
            height="714"
            loading="eager"
          />
        </Reveal>
      </header>

      <LogoRow />
      <FactStrip facts={facts} />
      <PreviewVideo blurb="This is Rob teaching live. Your purchase unlocks completed recordings from prior AI Bot Builder cohorts." />

      <section className="cp-section cp-learn" data-testid="what-youll-learn">
        <Reveal>
          <h2>What you'll learn</h2>
        </Reveal>
        <div className="cp-deliverable-grid">
          {SELF_PACED_DELIVERABLES.map((item, index) => (
            <Reveal key={item.title} delay={index * 55}>
              <article className="cp-deliverable-card">
                <div className="cp-deliverable-media" aria-hidden="true">
                  <img
                    className="cp-deliverable-img"
                    src={item.img}
                    alt=""
                    width="1280"
                    height="800"
                    loading="lazy"
                  />
                </div>
                <div className="cp-deliverable-copy">
                  <span className="cp-deliverable-num" aria-hidden="true">
                    {index + 1}
                  </span>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.detail}</p>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={320}>
          <ul className="cp-skill-pills" aria-label="Course skills">
            {SELF_PACED_SKILLS.map((skill) => (
              <li key={skill}>{skill}</li>
            ))}
          </ul>
        </Reveal>
      </section>

      {/* The archive spans multiple completed cohorts. Keep this stable outline
          independent of environment-specific historical and QA rows. */}
      <section className="cp-section cp-curriculum" id="curriculum" data-testid="curriculum">
        <Reveal>
          <h2>Archive curriculum</h2>
        </Reveal>
        <CurriculumAccordion
          sessions={SELF_PACED_ARCHIVE_MODULES}
          formatDate={null}
          sessionNoun="core modules"
          metaNote="covered across completed cohorts - watch on your own schedule"
        />
      </section>

      <section className="cp-section cp-instructor" aria-labelledby="instructor-heading">
        <Reveal>
          <h2 id="instructor-heading">{ROB_STORY.heading}</h2>
          <p className="cp-instructor-lede">
            Recorded from live cohort sessions taught by the founder.
          </p>
        </Reveal>
        <Reveal delay={70}>
          <div className="cp-instructor-body">
            <aside className="cp-instructor-aside">
              <img
                src="/images/rob.jpg"
                alt="Rob Grzesik"
                width="220"
                height="220"
                decoding="async"
              />
              <ul className="cp-instructor-creds">
                {ROB_STORY.credentials.map((cred) => (
                  <li key={cred.label}>
                    <strong>{cred.value}</strong>
                    <span>{cred.label}</span>
                  </li>
                ))}
              </ul>
            </aside>
            <div className="cp-instructor-copy">
              <h3>Rob Grzesik</h3>
              <p className="cp-instructor-role">{ROB_STORY.roleLine}</p>
              {ROB_STORY.paragraphs.map((para) => (
                <p key={para.slice(0, 48)}>{para}</p>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      <section className="cp-section cp-included" data-testid="whats-included">
        <Reveal>
          <h2>What's included</h2>
        </Reveal>
        <ul className="cp-included-list">
          {included.map((item, index) => (
            <Reveal as="li" key={item.name} delay={index * 55}>
              <img
                className="cp-included-img"
                src={item.img}
                alt=""
                loading="lazy"
                width="512"
                height="512"
              />
              <div>
                <h3>{item.name}</h3>
                <p>{item.detail}</p>
              </div>
            </Reveal>
          ))}
        </ul>
      </section>

      <section className="cp-section">
        <Reveal>
          <h2>Self-paced or live?</h2>
        </Reveal>
        <Reveal delay={80}>
          <div className="cp-compare-wrap">
            <table className="cp-compare">
              <thead>
                <tr>
                  <th scope="col">What you get</th>
                  <th scope="col">Self-paced</th>
                  <th scope="col" className="cp-col-live">
                    Live cohort <span>most popular</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row) => (
                  <tr key={row.feature}>
                    <td>{row.feature}</td>
                    <td>
                      {row.selfPaced ? (
                        <span className="cp-yes">Yes</span>
                      ) : (
                        <span className="cp-no">No</span>
                      )}
                    </td>
                    <td className="cp-col-live">
                      {row.live ? (
                        <span className="cp-yes">Yes</span>
                      ) : (
                        <span className="cp-no">No</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
        <Reveal delay={140}>
          <p className="cp-note">
            If you can attend on the session nights, the live cohort is worth it: you get your own
            questions answered and your setup reviewed.{' '}
            <Link to={LIVE_COURSE_PATH}>
              See the live cohort{upfrontPlan?.display ? ` from ${upfrontPlan.display}` : ''}
            </Link>
          </p>
        </Reveal>
      </section>

      <section className="cp-section cp-fit">
        <Reveal>
          <div>
            <h3>Self-paced is right if</h3>
            <ul>
              <li>The live session times do not work for you</li>
              <li>You are in a timezone where evenings Eastern are the middle of the night</li>
              <li>You prefer working through material on your own</li>
            </ul>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <div>
            <h3>Choose live instead if</h3>
            <ul>
              <li>You want your own questions answered as they come up</li>
              <li>You want Rob to look at your actual setup</li>
              <li>You know you finish things when other people are expecting you</li>
            </ul>
          </div>
        </Reveal>
      </section>

      <Reveal>
        <div className="cp-cta-band">
          <p>Start tonight with the completed AI Bot Builder recording archive.</p>
          <button
            type="button"
            className="cp-btn-primary"
            data-testid="buy-self-paced-mid"
            onClick={() => buy('after_curriculum')}
            disabled={buyDisabled}
          >
            Get the self-paced course
          </button>
        </div>
      </Reveal>

      <section className="cp-section cp-reviews" data-testid="student-reviews">
        <Reveal>
          <div className="cp-reviews-head">
            <h2>Student reviews</h2>
            <StarRating rating={4.6} compact />
          </div>
        </Reveal>
        <Reveal delay={40}>
          <p className="cp-reviews-lede">Hear from students who took the course.</p>
        </Reveal>
        <div className="cp-video-reviews" data-testid="video-reviews">
          {VIDEO_REVIEWS.map((review, index) => (
            <Reveal key={review.youtubeId} delay={index * 50} className="cp-video-review">
              <div className="cp-video-frame">
                <iframe
                  title={review.title}
                  src={`https://www.youtube-nocookie.com/embed/${review.youtubeId}?rel=0`}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <p className="cp-video-caption">{review.name}</p>
            </Reveal>
          ))}
        </div>
        <div className="cp-reviews-wall">
          {REVIEW_SHOTS.map((shot, index) => (
            <Reveal key={shot.src} delay={index * 60} className="cp-review-shot">
              <a
                href={TRUSTPILOT_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ '--tilt': `${shot.tilt}deg` }}
              >
                <img src={shot.src} alt={shot.alt} loading="lazy" />
              </a>
            </Reveal>
          ))}
        </div>
        <Reveal delay={380}>
          <p className="cp-reviews-more">
            <a href={TRUSTPILOT_URL} target="_blank" rel="noopener noreferrer">
              Read all reviews on Trustpilot
            </a>
          </p>
        </Reveal>
      </section>

      <section className="cp-section cp-pricing" id="pricing" data-testid="self-paced-pricing">
        <Reveal>
          <h2>Get started</h2>
        </Reveal>
        <Reveal delay={80}>
          <div className="cp-pricing-card">
            {promoActive ? (
              <div className="cp-pricing-promo">
                <p className="cp-pricing-promo-note">
                  {promoTierName || 'Current pricing'}
                  {selfPaced.originalDisplay ? (
                    <>
                      {': '}
                      <s>{selfPaced.originalDisplay}</s> <strong>{selfPaced.display}</strong>
                      {' one-time'}
                    </>
                  ) : null}
                </p>
                <PromoCountdown
                  endsAt={promoEndsAt}
                  label={`${promoTierName || 'Current pricing'} ends in`}
                  variant="blocks"
                />
              </div>
            ) : null}
            {pricingError || !selfPaced ? (
              <p className="cp-price-error" role="alert">
                We could not load current pricing. Rather than show you a price that might be wrong,
                we would rather say so. Please refresh, or contact us and we will get you enrolled.
              </p>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                {priceBlock('self-paced-pricing-price')}
              </div>
            )}
            <button
              type="button"
              className="cp-btn-primary cp-btn-block"
              data-testid="buy-self-paced-pricing"
              onClick={() => buy('pricing')}
              disabled={buyDisabled}
            >
              Get the self-paced course
            </button>
            <p className="ck-nocode cp-nocode-cta">{NO_CODING_BADGE}</p>
            <p className="cp-pricing-links">
              <Link to={LIVE_COURSE_PATH}>Prefer live sessions? See the cohort</Link>
            </p>
          </div>
        </Reveal>
      </section>

      <section className="cp-section cp-faq">
        <Reveal>
          <h2>Questions people ask</h2>
        </Reveal>
        <dl>
          {FAQS.map((f, i) => (
            <Reveal as="div" key={f.q} delay={i * 40}>
              <dt>{f.q}</dt>
              <dd>{f.a}</dd>
            </Reveal>
          ))}
        </dl>
      </section>

      <section
        className="cp-section cp-final"
        style={{
          backgroundImage:
            'linear-gradient(rgba(6,11,22,0.72), rgba(6,11,22,0.88)), ' +
            'url("/images/courses/final-cta-bg.webp")',
        }}
      >
        <Reveal>
          <h2>Start learning tonight</h2>
          <p>Completed AI Bot Builder recordings, on your schedule.</p>
          <div className="cp-final-actions">
            <button
              type="button"
              className="cp-btn-primary"
              data-testid="buy-self-paced-final"
              onClick={() => buy('final')}
              disabled={buyDisabled}
            >
              <span className="cp-btn-label">Get the self-paced course</span>
            </button>
            <Link className="cp-btn-ghost" to={LIVE_COURSE_PATH}>
              See the live cohort
            </Link>
          </div>
          <p className="ck-nocode cp-nocode-cta">{NO_CODING_BADGE}</p>
        </Reveal>
      </section>

      <div className="cp-sticky">
        <span data-testid="sticky-price">
          {isLoading ? (
            ''
          ) : pricingError || !selfPaced ? (
            'Pricing unavailable'
          ) : (
            <>
              {selfPaced.hasDiscount && selfPaced.originalDisplay ? (
                <>
                  <s>{selfPaced.originalDisplay}</s>{' '}
                </>
              ) : null}
              {`${selfPaced.display} one-time`}
            </>
          )}
        </span>
        <button
          type="button"
          className="cp-btn-primary"
          data-testid="buy-self-paced-sticky"
          onClick={() => buy('sticky')}
          disabled={buyDisabled}
        >
          Get the course
        </button>
      </div>

      <CourseLegalDisclaimer subject="This material" />
    </div>
  );
};

export default CourseSelfPacedPage;
